// The card's aggregator: the period's scanned games where the reader stood
// winning before one of their middlegame moves (`GameFacts.conversion`, at
// the Winning setting, the standing Middlegame results calls winning), by
// outcome, and those not won by where the lead went (the `conversion` sets
// of `conversionsOf`: Tactic, Missed chance, Time, Positional, or no single
// mistake), so every count is the library's for the same filter. Registered
// with the game store by this plugin's setup; the store runs it over the
// last 30 days' games on every change and keeps the result.
import { aggregatorId, collect, conversionsOf, GROUND_CAUSES, type Accumulator, type Aggregator, type GameOutcome, type GroundCause } from '@chess/api-model';

/** Where a lead went: a cause of lost ground, or `none` when no counted mistake explains it. */
export type LeadCause = GroundCause | 'none';
export const LEAD_CAUSES = [...GROUND_CAUSES, 'none'] as const satisfies readonly LeadCause[];

/** What the card shows; plain data, so the card and its stories need no store. */
export interface MiddlegameConversionResult {
  /** The games ahead in the middlegame, by the reader's outcome. */
  ahead: Record<GameOutcome, number>;
  /** Those not won, by where the lead went; they add up to the draws and losses. */
  byCause: Record<LeadCause, number>;
}

export class MiddlegameConversionAggregator implements Aggregator<MiddlegameConversionResult> {
  readonly id = aggregatorId('middlegame-conversion');

  start(): Accumulator<MiddlegameConversionResult> {
    return collect((games) => {
      const ahead: Record<GameOutcome, number> = { win: 0, draw: 0, loss: 0 };
      const byCause: Record<LeadCause, number> = { tactic: 0, missed: 0, time: 0, positional: 0, none: 0 };
      for (const g of games) {
        const sets = conversionsOf(g);
        if (!sets.includes('ahead')) continue;
        ahead[g.outcome] += 1;
        for (const c of LEAD_CAUSES) if (sets.includes(c)) byCause[c] += 1;
      }
      return { ahead, byCause };
    });
  }
}

/** The games ahead in the middlegame. */
export const aheadOf = (r: MiddlegameConversionResult) => r.ahead.win + r.ahead.draw + r.ahead.loss;
/** Those not won. */
export const notWonOf = (r: MiddlegameConversionResult) => r.ahead.draw + r.ahead.loss;

/** The card shows at most this many causes: the largest. */
export const MAX_CAUSE_ROWS = 3;

/**
 * The causes the card shows as rows: those with at least one game, largest
 * first (a tie in the causes' own order), at most `MAX_CAUSE_ROWS`; never
 * `none` (no single mistake), which the help explains instead.
 */
export function causeRowsOf(r: MiddlegameConversionResult): Array<{ cause: GroundCause; count: number }> {
  return GROUND_CAUSES.map((cause) => ({ cause, count: r.byCause[cause] }))
    .filter((c) => c.count > 0)
    .sort((a, b) => b.count - a.count)
    .slice(0, MAX_CAUSE_ROWS);
}
